import { useLayoutEffect, useRef, type KeyboardEvent, type RefObject } from 'react'
import { focusAfterLayout } from '../../../app/lib/focusTrap'

export function showPlane(node: HTMLDialogElement): void {
  node.removeAttribute('aria-hidden'); node.inert = false
  if (typeof node.showPopover === 'function') {
    if (!node.hasAttribute('popover')) node.setAttribute('popover', 'manual')
    node.showPopover()
    node.setAttribute('open', ''); node.setAttribute('aria-modal', 'true')
  } else node.showModal()
}
export function closePlane(node: HTMLDialogElement): void {
  node.setAttribute('aria-hidden', 'true'); node.inert = true
  if (node.hasAttribute('popover')) { node.hidePopover(); node.removeAttribute('open') }
  else node.close()
  node.removeAttribute('aria-modal')
}

/** A native top layer and bounded focus ownership avoid showModal's whole-document inert restyle. */
export function useModal(open: boolean, initialFocus?: RefObject<HTMLElement | null>) {
  const dialog = useRef<HTMLDialogElement>(null)
  const restore = useRef<HTMLElement | null>(null)
  useLayoutEffect(() => {
    if (typeof dialog.current?.showPopover === 'function') dialog.current.setAttribute('popover', 'manual')
  }, [])
  useLayoutEffect(() => {
    const node = dialog.current
    if (!node) return
    let cancelFocus = () => {}
    let opening: ResizeObserver | undefined
    if (open && !node.open) {
      restore.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
      const reveal = () => {
        opening?.disconnect()
        showPlane(node)
        const target = initialFocus?.current ?? node.querySelector<HTMLElement>('button,input,select')
        if (target) cancelFocus = focusAfterLayout(target)
      }
      if (typeof node.showPopover === 'function' && typeof ResizeObserver !== 'undefined') {
        opening = new ResizeObserver(reveal); opening.observe(node)
      } else reveal()
    } else if (!open && node.open) {
      closePlane(node)
      if (restore.current?.isConnected) cancelFocus = focusAfterLayout(restore.current)
    }
    if (!open) return cancelFocus
    const focus = (event: FocusEvent) => {
      if (node.open && event.target instanceof Node && !node.contains(event.target)) (initialFocus?.current ?? node.querySelector<HTMLElement>('button,input,select'))?.focus({ preventScroll: true })
    }
    const wheel = (event: WheelEvent | TouchEvent) => { if (event.target === node) event.preventDefault() }
    document.addEventListener('focusin', focus)
    node.addEventListener('wheel', wheel, { passive: false }); node.addEventListener('touchmove', wheel, { passive: false })
    return () => { opening?.disconnect(); cancelFocus(); document.removeEventListener('focusin', focus); node.removeEventListener('wheel', wheel); node.removeEventListener('touchmove', wheel) }
  }, [open, initialFocus])
  return dialog
}

/** Bounded to this small dialog, including the non-inert native top-layer path. */
export function trapTab(event: KeyboardEvent<HTMLDialogElement>) {
  if (event.key === 'Escape' && event.currentTarget.hasAttribute('popover')) {
    event.preventDefault(); event.stopPropagation()
    event.currentTarget.dispatchEvent(new Event('cancel', { cancelable: true }))
    return
  }
  if (event.key !== 'Tab') return
  const controls = [...event.currentTarget.querySelectorAll<HTMLElement>('*')]
    .filter((node) => node.matches('button,input,select,summary,a[href],[tabindex="0"]') && !node.matches(':disabled') && !node.closest('[hidden]'))
  const first = controls[0], last = controls[controls.length - 1]
  if (!first || !last) { event.preventDefault(); return }
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
}
